'use client';

import type { ComponentType } from 'react';
import { MetaDefault, MonoSmall } from '@terra/ui-design-system';
import { Box, HStack, styled } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  BotIcon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  CpuIcon,
  HierarchyIcon,
  Loading03Icon,
  Settings02Icon,
  UserIcon,
  Wrench01Icon,
} from '@terra/ui-icons';
import type { AgentTraceNode, AgentTraceNodeKind } from './buildAgentTraceTree';

type KindVisual = {
  icon: ComponentType<{ size?: number; color?: string }>;
  accent: string;
  label: string;
};

const KIND_VISUALS: Record<AgentTraceNodeKind, KindVisual> = {
  chain: { icon: CpuIcon, accent: 'fg.muted', label: 'chain' },
  system: { icon: Settings02Icon, accent: 'gray.9', label: 'system' },
  human: { icon: UserIcon, accent: 'blue.9', label: 'human' },
  llm: { icon: BotIcon, accent: 'purple.9', label: 'llm' },
  tool: { icon: Wrench01Icon, accent: 'orange.9', label: 'tool' },
  subagent: { icon: HierarchyIcon, accent: 'teal.9', label: 'sub-agent' },
};

const Row = styled('button', {
  base: {
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 'sm',
    transition: 'background 0.12s ease, border-color 0.12s ease',
    _hover: { bg: 'bg.subtle' },
  },
});

const ChevronButton = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    borderRadius: 'xs',
    color: 'fg.muted',
    _hover: { color: 'fg.default' },
  },
});

function StatusIcon({ status }: { status?: string }) {
  if (!status) {
    return null;
  }

  if (status === 'success') {
    return <CheckmarkCircle02Icon size={13} color="var(--colors-green-9)" />;
  }

  if (status === 'error') {
    return <CancelCircleIcon size={13} color="var(--colors-red-9)" />;
  }

  if (status === 'pending') {
    return <Loading03Icon size={13} color="var(--colors-fg-muted)" />;
  }

  return null;
}

type AgentTraceTreeProps = {
  root: AgentTraceNode;
  selectedId: string | null;
  expandedIds: ReadonlySet<string>;
  onSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
};

function TraceRow({
  node,
  depth,
  selectedId,
  expandedIds,
  onSelect,
  onToggleExpand,
}: {
  node: AgentTraceNode;
  depth: number;
} & Omit<AgentTraceTreeProps, 'root'>) {
  const visual = KIND_VISUALS[node.kind];
  const Icon = visual.icon;
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;

  return (
    <Box>
      <Row
        type="button"
        onClick={() => onSelect(node.id)}
        bg={isSelected ? 'bg.emphasized' : 'transparent'}
        borderColor={isSelected ? 'border.default' : 'transparent'}
        px={1.5}
        py={1.5}
        style={{ paddingLeft: `${depth * 14 + 6}px` }}
      >
        <HStack gap={1.5} alignItems="center" minW={0}>
          {hasChildren ? (
            <ChevronButton
              role="button"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              onClick={(event) => {
                event.stopPropagation();
                onToggleExpand(node.id);
              }}
            >
              {isExpanded ? (
                <ArrowDown01Icon size={14} />
              ) : (
                <ArrowRight01Icon size={14} />
              )}
            </ChevronButton>
          ) : (
            <Box w="16px" h="16px" flexShrink={0} />
          )}

          <Box color={visual.accent} display="flex" flexShrink={0}>
            <Icon size={14} />
          </Box>

          <MonoSmall
            color={node.skipped ? 'fg.subtle' : 'fg.default'}
            fontWeight="medium"
            whiteSpace="nowrap"
            textDecoration={node.skipped ? 'line-through' : undefined}
          >
            {node.name}
          </MonoSmall>

          <StatusIcon status={node.status} />

          <MetaDefault color="fg.muted" lineClamp={1} flex="1" minW={0}>
            {node.preview}
          </MetaDefault>
        </HStack>
      </Row>

      {hasChildren && isExpanded && (
        <Box>
          {node.children.map((child) => (
            <TraceRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

export function AgentTraceTree({
  root,
  selectedId,
  expandedIds,
  onSelect,
  onToggleExpand,
}: AgentTraceTreeProps) {
  return (
    <Box role="tree" py={1}>
      <TraceRow
        node={root}
        depth={0}
        selectedId={selectedId}
        expandedIds={expandedIds}
        onSelect={onSelect}
        onToggleExpand={onToggleExpand}
      />
    </Box>
  );
}
